import { createClient, type SupabaseClient } from "@supabase/supabase-js";

let client: SupabaseClient | null = null;
let config: { emailTestMode?: boolean; smsProvider?: string; smsTestMode?: boolean } = {};
/** True while every email is redirected to the owner's test inbox (EMAIL_REDIRECT_ALL_TO). */
export const emailTestMode = () => !!config.emailTestMode;
/** Which SMS provider the Worker uses ("none" until texting is set up) and whether texts are redirected. */
export const smsConfig = () => ({ provider: config.smsProvider ?? "none", testMode: !!config.smsTestMode });

/** Loads public config from the Worker and creates the browser client (RLS applies). */
export async function initSupabase(): Promise<SupabaseClient> {
  const res = await fetch("/api/config");
  if (!res.ok) throw new Error("Could not load app configuration");
  const cfg = (await res.json()) as { supabaseUrl: string; supabaseKey: string; emailTestMode?: boolean; smsProvider?: string; smsTestMode?: boolean };
  config = cfg;
  client = createClient(cfg.supabaseUrl, cfg.supabaseKey, {
    // detectSessionInUrl + implicit: accepts the #access_token redirect from Supabase's built-in
    // magic-link email (interim mode). Our own links use /auth/confirm + verifyOtp instead.
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: "implicit" },
  });
  return client;
}

export function sb(): SupabaseClient {
  if (!client) throw new Error("Supabase not initialised");
  return client;
}

/** Calls an authenticated Worker endpoint. */
export async function api<T = unknown>(path: string, body?: unknown): Promise<T> {
  const { data } = await sb().auth.getSession();
  const res = await fetch(path, {
    method: body === undefined ? "GET" : "POST",
    headers: {
      "content-type": "application/json",
      ...(data.session ? { authorization: `Bearer ${data.session.access_token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new Error(json.error ?? `Request failed (${res.status})`);
  return json;
}

/** Throws the Supabase error (if any) so callers can use try/catch. */
export function must<T>(r: { data: T; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return r.data;
}
